import { MODULE_TYPES } from "../../modules/types"
import type { DashboardModule } from "../../modules/types"

const FALLBACK_MODULE_TITLE = "AI Artifact"

type SuccessfullyPinnedArtifactModule = DashboardModule & {
  type: typeof MODULE_TYPES.AI_ARTIFACT
  config: DashboardModule["config"] & {
    artifactId: string
    pinnedId: string
    hasError?: false
  }
}

export const isSuccessfullyPinnedArtifactModule = (
  module: DashboardModule,
): module is SuccessfullyPinnedArtifactModule =>
  module.type === MODULE_TYPES.AI_ARTIFACT
  && typeof module.config?.artifactId === "string"
  && typeof module.config?.pinnedId === "string"
  && module.config?.hasError !== true

export const getPinnedArtifactIds = (modulesList: DashboardModule[]) =>
  modulesList
    .filter(isSuccessfullyPinnedArtifactModule)
    .map((module) => module.config.artifactId)

export function normalizeModuleTitle(title: unknown): string {
  return typeof title === "string" ? title.trim() : ""
}

export function getUniqueModuleTitle(baseTitle: string | undefined, modules: DashboardModule[]): string {
  const normalizedBaseTitle = normalizeModuleTitle(baseTitle) || FALLBACK_MODULE_TITLE
  const existingTitleSet = new Set(
    modules
      .map(module => normalizeModuleTitle(module?.config?.title))
      .filter(Boolean),
  )

  if (!existingTitleSet.has(normalizedBaseTitle)) {
    return normalizedBaseTitle
  }

  let duplicateCounter = 2
  const maxDuplicateCounter = existingTitleSet.size + 1
  let candidateTitle = `${normalizedBaseTitle} ${duplicateCounter}`

  while (existingTitleSet.has(candidateTitle) && duplicateCounter <= maxDuplicateCounter) {
    duplicateCounter += 1
    candidateTitle = `${normalizedBaseTitle} ${duplicateCounter}`
  }

  if (!existingTitleSet.has(candidateTitle)) {
    return candidateTitle
  }

  // Defensive fallback; should be unreachable for finite module sets.
  return `${normalizedBaseTitle} ${maxDuplicateCounter + 1}`
}
